import { Box, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { SeamlessTextArea } from '@databases/form/styled';
import { Metrics } from '@databases/frontend-metrics';
import { useTypingPerformance } from '@databases/frontend-metrics/useTypingPerformance';
import { useTextDelta } from '@databases/react/useTextDelta';
import { defaultStoreUtils } from '@databases/redux';
import { selectFieldIdsByContentId } from '@databases/redux/cellSelections/selectors';
import { DatabaseFieldType } from '@databases/types/database';
import composeRefs from '@seznam/compose-react-refs';
import { type Op } from 'quill-delta';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  MAX_POPOVER_EDIT_WIDTH,
  TEXT_VALUE_MAX_TEXT_AREA_HEIGHT,
  useAdjustTextAreaDimensions,
} from './popover-edit-common';
import { type EditComponentProps } from './types';

const textAreaContainerStyling = xcss({
  display: 'flex',
  flexDirection: 'row',
  boxSizing: 'border-box',
  position: 'relative',
  zIndex: 'dialog', // Ensure container sits above blue dots
});

const lastFieldStylings = xcss({
  flexDirection: 'row-reverse',
});

const textAreaMinHeightStyling = xcss({
  minHeight: 'size.400',
});

const TextValuePopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.TEXT>
> = ({
  field,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  isSeamless,
  isRenderedInSidePanel,
  contentId,
  isBulkEdit,
}) => {
  const maxWidthReachedRef = useRef<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState(value);
  const store = defaultStoreUtils.useStore();

  const adjustTextAreaDimensions = useAdjustTextAreaDimensions({
    textAreaRef,
    maxWidthReachedRef,
    maxPopoverEditWidth: isRenderedInSidePanel
      ? textAreaRef.current?.scrollWidth
      : MAX_POPOVER_EDIT_WIDTH,
  });

  useEffect(() => {
    adjustTextAreaDimensions();
  }, [inputValue, adjustTextAreaDimensions]);

  const fieldIds = selectFieldIdsByContentId(store.getState(), {
    contentId,
  });

  const isLastField = fieldIds.indexOf(field.id) === fieldIds.length - 1;

  const Component = isSeamless ? SeamlessTextArea : TextArea;

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Put cursor to the end of the field
      e.currentTarget.setSelectionRange(
        e.currentTarget.value.length,
        e.currentTarget.value.length
      );

      adjustTextAreaDimensions();
      onFocus?.(e);
    },
    [onFocus, adjustTextAreaDimensions]
  );

  useTextDelta(
    textAreaRef.current,
    useCallback(
      (delta: Op[], newValue: string) => {
        setInputValue(newValue);
        onChange(newValue, {
          type: DatabaseFieldType.TEXT,
          delta,
        });
      },
      [onChange]
    )
  );

  const typingPerformanceRef = useTypingPerformance<HTMLTextAreaElement>(
    Metrics.MEAN_TEXT_VALUE_TYPING
  );
  return (
    <Box
      testId={`text-value-edit-expand-${isLastField ? 'left' : 'right'}`}
      xcss={[
        textAreaContainerStyling,
        isLastField && lastFieldStylings,
        isRenderedInSidePanel && textAreaMinHeightStyling,
      ]}
    >
      <Component
        value={inputValue}
        onFocus={handleFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        resize="none"
        wrap="off"
        ref={composeRefs(textAreaRef, typingPerformanceRef)}
        testId={'text-value-edit'}
        style={{
          border: `${token('border.width.selected', '2px')} solid ${token(
            'color.background.accent.blue.subtle',
            '#579DFF'
          )}`,
          borderRadius: 'radius.small',
          overflow: 'hidden',
          position: isRenderedInSidePanel || isBulkEdit ? 'relative' : 'absolute',
          boxSizing: 'border-box',
          maxHeight: `${TEXT_VALUE_MAX_TEXT_AREA_HEIGHT}px`,
        }}
      />
    </Box>
  );
};

export default TextValuePopoverEdit;
